/**
 * Team codes: how a student team signs in without an account.
 *
 * The lead makes a team in the admin screen; the app shows the code ONCE and
 * stores only `sha256(hashInput)` in `teams.code_hash`. The team types the
 * project code (e.g. DS) and the team code (e.g. K7M3PX); `claim_team()` in
 * migration 0002 recomputes the hash with pgcrypto and, on a match, stamps the
 * anonymous session's app_metadata with team_id / project_id / team_name /
 * team_expires_at. Hashing itself is done by whichever crypto the caller has
 * (WebCrypto on the web, expo-crypto on the phone, pgcrypto in SQL); this module
 * only fixes the alphabet and the exact string that gets hashed, so all three agree.
 */

/** Unambiguous upper-case alphabet: no 0/O, 1/I/L. */
export const TEAM_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const TEAM_CODE_LENGTH = 6;

/** Upper-case, strip spaces and dashes, so "k7m-3px" and "K7M3PX" are the same code. */
export function normalizeTeamCode(code: string): string {
  return code.toUpperCase().replace(/[\s-]+/g, '');
}

export function isValidTeamCode(code: string): boolean {
  const c = normalizeTeamCode(code);
  return c.length === TEAM_CODE_LENGTH && [...c].every((ch) => TEAM_CODE_ALPHABET.includes(ch));
}

/**
 * The exact string whose SHA-256 (hex, lower-case) is stored in teams.code_hash.
 * Salted with the project id so the same code on two projects never shares a hash.
 * MUST match `claim_team()` in supabase/migrations/0002_*.sql.
 */
export function teamCodeHashInput(projectId: string, code: string): string {
  return `${projectId}:${normalizeTeamCode(code)}`;
}

/** A fresh code from a source of random bytes (crypto.getRandomValues or expo-crypto). */
export function generateTeamCode(randomBytes: (n: number) => Uint8Array): string {
  const bytes = randomBytes(TEAM_CODE_LENGTH);
  let out = '';
  for (let i = 0; i < TEAM_CODE_LENGTH; i++) out += TEAM_CODE_ALPHABET[bytes[i] % TEAM_CODE_ALPHABET.length];
  return out;
}

/** Display form, split for reading aloud: K7M-3PX. */
export function formatTeamCode(code: string): string {
  const c = normalizeTeamCode(code);
  return c.length === TEAM_CODE_LENGTH ? `${c.slice(0, 3)}-${c.slice(3)}` : c;
}
